import Phaser from 'phaser';

import { GameScene, type GameSceneData } from './scenes/game-scene.ts';
import { showLoginScreen } from './ui/login-screen.ts';

/**
 * Entrada do cliente.
 *
 * A ordem importa: **primeiro** a tela de login em DOM, e só com um token de sessão na
 * mão o Phaser sobe. Iniciar o motor antes gastaria a geração de todos os atlas e a
 * criação do contexto WebGL para um jogador que ainda vai digitar a senha — e, se ele
 * errasse, para nada.
 */

const authRoot = document.getElementById('auth');
if (!authRoot) throw new Error('elemento #auth não existe no HTML');

const outcome = await showLoginScreen(authRoot);

document.getElementById('interface')?.classList.add('visible');

const sceneData: GameSceneData = {
  sessionToken: outcome.sessionToken,
  vocation: outcome.vocation,
  accountToken: outcome.accountToken,
  characterId: outcome.characterId,
  ...(outcome.gamePort === undefined ? {} : { gamePort: outcome.gamePort }),
};

const gameRoot = document.getElementById('game');
if (!gameRoot) throw new Error('elemento #game não existe no HTML');

new Phaser.Game({
  type: Phaser.AUTO,
  // O elemento, e não o id: é o mesmo objeto que `followParentSize` observa, e assim não
  // há como o Phaser medir uma caixa e o observador medir outra.
  parent: gameRoot,
  backgroundColor: '#0b0f14',
  // `pixelArt` desliga a suavização de textura: a arte é feita de pixels inteiros e
  // interpolar borraria o contorno que dá a silhueta.
  pixelArt: true,
  roundPixels: true,
  // **O menu do navegador tapava o do jogo.** `GameScene` abre o menu de contexto no
  // `pointerdown` do botão direito (PRD §15.4), mas o `contextmenu` do navegador dispara logo
  // depois, por cima, e o primeiro clique em qualquer item dele fecha os dois. O sintoma não
  // é "o menu não abre" — é que a única forma de mandar `UseTile` ficou inalcançável, e com
  // ela porta, alavanca e baú: uma porta fechada não podia ser aberta de dentro do jogo.
  //
  // O Phaser não desliga isso sozinho; o padrão de `disableContextMenu` é `false`.
  disableContextMenu: true,
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [GameScene],
  callbacks: {
    preBoot: (game) => {
      game.scene.start('game', sceneData);
    },
    // `postBoot`, e não `preBoot`: antes do boot o ScaleManager ainda não tem canvas, e é
    // nele que `setParentSize` mexe.
    postBoot: (game) => {
      followParentSize(game, gameRoot);
    },
  },
});

/**
 * Mantém o buffer do canvas do tamanho da caixa em que ele é desenhado.
 *
 * O `Phaser.Scale.RESIZE` não escuta o elemento: ele guarda a última medida do pai e a
 * compara com a atual numa sondagem de 500 ms. O que estraga é o fim de `updateScale`, que
 * relê o pai e **atualiza a medida guardada sem redimensionar nada** — um `refresh` que
 * caia depois de o layout mudar deixa o buffer no tamanho velho e ainda faz a sondagem
 * seguinte não ver diferença nenhuma. Para sempre.
 *
 * E cai em todo carregamento, sem sorte envolvida: no evento `READY` o SceneManager está
 * inscrito antes do ScaleManager, então `GameScene.create` monta a barra de magias e
 * encolhe a linha do viewport **dentro do mesmo evento**, uma chamada antes do `refresh`
 * que ainda acredita no tamanho anterior. Como o CSS estica o canvas para caber
 * (`#game canvas { height: 100% }`), o erro não aparece como corte e sim como tile
 * retangular e clique deslocado — as medições em três janelas estão no PRD §14.1.
 *
 * O observador tira a medida do caminho da sondagem: `setParentSize` **entrega** o tamanho
 * já medido, em vez de pedir que o Phaser o descubra. Vale para qualquer motivo de
 * mudança — a fonte que termina de carregar, um painel que abre, a barra do navegador do
 * celular mexendo com `100dvh` —, e não só para a barra de magias.
 *
 * Reservar a altura da barra no CSS não substituiria isto: a linha depende do nome de
 * magia mais longo da vocação, que quebra dentro do botão (medi 56 px com nome de uma
 * linha e 66 px com nome quebrado), então qualquer constante fica errada para alguma
 * vocação — e continuaria errada quando a fonte trocasse ou o `#help` sumisse no
 * `@media`.
 */
function followParentSize(game: Phaser.Game, parent: HTMLElement): void {
  const observer = new ResizeObserver(() => {
    // `getBoundingClientRect`, e não o `contentRect` da entrada: é o que o próprio
    // ScaleManager mede, e este defeito nasceu de duas medidas discordando.
    const rect = parent.getBoundingClientRect();

    // Arredondado, porque `canvas.width` é inteiro: passar 1102,5 faz o navegador truncar o
    // buffer para 1102 enquanto o `baseSize` do Phaser guarda 1102,5, e a escala do ponteiro
    // sai desse meio pixel de desacordo. Melhor as duas pontas concordarem num inteiro.
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    // Caixa zerada é interface escondida, não medida: repassá-la zeraria o viewport do
    // WebGL e o quadro seguinte sairia em branco.
    if (width <= 0 || height <= 0) return;

    // `setParentSize` chama `refresh`, que redimensiona o buffer, avisa o renderer e emite
    // `Scale.Events.RESIZE` — de onde a cena já refaz o zoom sozinha.
    game.scale.setParentSize(width, height);
  });

  observer.observe(parent);
  game.events.once(Phaser.Core.Events.DESTROY, () => observer.disconnect());
}
